import { useState, MouseEvent } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';
import BookmarksOutlinedIcon from '@mui/icons-material/BookmarksOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useSavedFilters } from '../../hooks/useSavedFilters';

interface SavedFiltersBarProps<T extends Record<string, unknown>> {
  pageKey: string;
  currentFilters: T;
  onApply: (filters: T) => void;
}

export function SavedFiltersBar<T extends Record<string, unknown>>({
  pageKey,
  currentFilters,
  onApply,
}: SavedFiltersBarProps<T>) {
  const { presets, savePreset, deletePreset } = useSavedFilters<T>(pageKey);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');

  const hasActiveFilters = Object.values(currentFilters).some(
    (v) => v !== undefined && v !== '' && v !== null,
  );

  const handleClose = () => {
    setAnchorEl(null);
    setNaming(false);
    setName('');
  };

  const handleSave = () => {
    if (!name.trim()) return;
    savePreset(name.trim(), currentFilters);
    handleClose();
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<BookmarksOutlinedIcon fontSize="small" />}
        onClick={(e: MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget)}
      >
        Saved filters{presets.length > 0 ? ` (${presets.length})` : ''}
      </Button>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleClose} slotProps={{ paper: { sx: { width: 300 } } }}>
        {naming ? (
          <Box sx={{ px: 2, py: 1 }}>
            <Stack direction="row" spacing={1}>
              <TextField
                autoFocus
                size="small"
                fullWidth
                placeholder="Filter name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              />
              <Button size="small" variant="contained" onClick={handleSave} disabled={!name.trim()}>
                Save
              </Button>
            </Stack>
          </Box>
        ) : (
          <MenuItem disabled={!hasActiveFilters} onClick={() => setNaming(true)}>
            <BookmarkAddOutlinedIcon fontSize="small" sx={{ mr: 1.5 }} />
            Save current filters
          </MenuItem>
        )}
        <Divider />
        {presets.length === 0 ? (
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              No saved filters yet
            </Typography>
          </Box>
        ) : (
          presets.map((preset) => (
            <MenuItem
              key={preset.id}
              onClick={() => {
                onApply(preset.filters);
                handleClose();
              }}
              sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}
            >
              <Chip size="small" variant="outlined" label={preset.name} sx={{ maxWidth: 200 }} />
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  deletePreset(preset.id);
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
}
